/**
 * Подписи сущностей LTMS по языку переключателя раскладки.
 * Заголовок карточки, история, Audit Trail и модалка удаления берут имя отсюда.
 */
import { localizedPair, productDisplayName } from '@/lib/productCard'

/**
 * Английская раскладка интерфейса.
 *
 * @param {string} locale код vue-i18n (`en`, `ru`).
 * @returns {boolean} true для en*.
 */
export function uiPrefersEnglish(locale) {
  return String(locale ?? '').toLowerCase().startsWith('en')
}

/**
 * Текст скаляра или подпись dropdown (`value`). Прочерк-заглушку считает пустым.
 *
 * @param {unknown} value
 * @returns {string}
 */
function textOf(value) {
  if (value == null) {
    return ''
  }
  if (typeof value === 'object') {
    return String(value.value ?? '').trim()
  }
  const text = String(value).trim()
  return text === '-' ? '' : text
}

/**
 * Trade name на языке раскладки, без отката к МНН и коду молекулы.
 *
 * @param {object|null|undefined} product
 * @param {boolean} preferEnglish
 * @returns {string}
 */
export function tradeNameOnly(product, preferEnglish) {
  if (!product || typeof product !== 'object') {
    return ''
  }
  return localizedPair(product.trade_name_en, product.trade_name_ru, preferEnglish)
}

/**
 * Площадка: EN — Site name, RU — Alternative name.
 * Пустую сторону дополняет другая, чтобы заголовок не пропадал.
 *
 * @param {object|null|undefined} site
 * @param {boolean} preferEnglish
 * @returns {string}
 */
export function siteDisplayName(site, preferEnglish) {
  if (!site || typeof site !== 'object') {
    return ''
  }
  const en = textOf(site.ml_site_name_en)
  const ru = textOf(site.ml_site_name_ru)
  const alternative = textOf(site.alternative_name)
  if (preferEnglish) {
    return en || ru || alternative
  }
  return alternative || ru || en
}

/**
 * GMP Status List: EN — Site name связанной площадки, RU — Alternative name записи.
 *
 * @param {object|null|undefined} record
 * @param {boolean} preferEnglish
 * @returns {string}
 */
export function gmpStatusDisplayName(record, preferEnglish) {
  if (!record || typeof record !== 'object') {
    return ''
  }
  if (preferEnglish) {
    const site = record.site
    if (site && typeof site === 'object') {
      return textOf(site.ml_site_name_en) || textOf(site.ml_site_name_ru)
    }
    return ''
  }
  return textOf(record.alternative_name)
}

/**
 * Product в GMP for submission: ручной ввод либо подпись карточки PL.
 *
 * @param {object|null|undefined} record
 * @param {boolean} preferEnglish
 * @returns {string}
 */
export function gmpProductTitle(record, preferEnglish) {
  if (!record || typeof record !== 'object') {
    return ''
  }
  if (record.product_manual) {
    return textOf(record.product_manual_value)
  }
  return (
    productDisplayName(record.product, preferEnglish) ||
    textOf(record.product_manual_value) ||
    textOf(record.product_name)
  )
}

/**
 * ТН или наименование ТН у ЛП: EN — name_eng, RU — name.
 *
 * @param {object|string|number|null|undefined} entity
 * @param {boolean} preferEnglish
 * @returns {string}
 */
export function catalogDisplayName(entity, preferEnglish) {
  if (entity == null || entity === '') {
    return ''
  }
  if (typeof entity === 'string' || typeof entity === 'number') {
    return textOf(entity)
  }
  if (typeof entity !== 'object') {
    return ''
  }
  const trademark = entity.medicine_trademark
  const source = trademark && typeof trademark === 'object' ? trademark : entity
  return (
    localizedPair(textOf(source.name_eng), textOf(source.name), preferEnglish) ||
    localizedPair(textOf(entity.name_eng), textOf(entity.name), preferEnglish)
  )
}

/**
 * Подпись related-объекта истории, если это продукт, площадка или справочник ТН/ЛП.
 *
 * @param {object} value
 * @param {boolean} preferEnglish
 * @returns {string} пустая строка, если объект другого вида.
 */
export function localizedRelationTitle(value, preferEnglish) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ''
  }
  if (
    'trade_name_en' in value ||
    'trade_name_ru' in value ||
    'inn_en' in value ||
    'inn_ru' in value ||
    'azd_code' in value
  ) {
    const title = productDisplayName(value, preferEnglish)
    if (title) {
      return title
    }
  }
  if ('ml_site_name_en' in value || 'ml_site_name_ru' in value) {
    const title = siteDisplayName(value, preferEnglish)
    if (title) {
      return title
    }
  }
  if ('name_eng' in value || (value.medicine_trademark && typeof value.medicine_trademark === 'object')) {
    const title = catalogDisplayName(value, preferEnglish)
    if (title) {
      return title
    }
  }
  return ''
}
